import React, { useState, useEffect } from 'react';
import { logError } from '@/lib/logger';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { MilestoneCelebration } from '@/components/MilestoneCelebration';
import { CrisisFooter } from '@/components/CrisisFooter';
import { Download } from 'lucide-react';

import {
  BottomNav,
  SageGreeting,
  EmotionalWeather,
  RitualTiles,
  StreakDisplay,
  WEATHER_OPTIONS,
  phaseInfo
} from '@/components/sanctuary';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DASHBOARD_REQUEST_TIMEOUT_MS = 15000;
const ANIMATE_IN_DELAY_MS = 100;
const COPY_SUCCESS_DISMISS_MS = 2000;

export const SanctuaryPage = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWeather, setSelectedWeather] = useState(null);
  const [weatherResponse, setWeatherResponse] = useState(null);
  const [animateIn, setAnimateIn] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const response = await axios.get(`${API}/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: DASHBOARD_REQUEST_TIMEOUT_MS,
        });
        setDashboard(response.data);
        if (response.data.today_mood) {
          setSelectedWeather(response.data.today_mood);
          const weather = WEATHER_OPTIONS.find(w => w.id === response.data.today_mood);
          if (weather) setWeatherResponse(weather.response);
        }
        
        try {
          const refResponse = await axios.get(`${API}/referral/code`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: DASHBOARD_REQUEST_TIMEOUT_MS,
          });
          setReferralCode(refResponse.data);
        } catch (refError) {
          // Referral is non-critical - continue without it
                      logError('Referral fetch failed:', refError?.response?.status);
        }
      } catch (error) {
        toast.error('Failed to load sanctuary');
                  logError('Sanctuary load error:', error?.message);
      } finally {
        setLoading(false);
        setTimeout(() => setAnimateIn(true), ANIMATE_IN_DELAY_MS);
      }
    };
    if (!authLoading) loadDashboard();
  }, [token, authLoading]);

  const handleCopyReferral = async () => {
    if (referralCode?.share_url) {
      try {
        await navigator.clipboard.writeText(referralCode.share_url);
        setCopySuccess(true);
        toast.success('Link copied!');
        setTimeout(() => setCopySuccess(false), COPY_SUCCESS_DISMISS_MS);
      } catch {
        toast.error('Failed to copy');
      }
    }
  };

  const handleWeatherSelect = async (weather) => {
    setSelectedWeather(weather.id);
    setWeatherResponse(weather.response);
    try {
      await axios.post(
        `${API}/mood/save`,
        { mood: weather.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Mood save failure is non-critical - user can continue
              logError('Mood save failed:', error?.response?.status);
    }
  };

  const getGreeting = () => {
    const name = user?.preferred_name || user?.name || 'there';
    const hour = new Date().getHours();
    const streak = dashboard?.streak || 0;
    const daysSinceLastVisit = dashboard?.days_since_last_visit || 0;

    if (streak > 0 && streak % 7 === 0) {
      return { primary: `${streak} days of showing up, ${name}.`, secondary: 'That is not a small thing.' };
    }
    if (daysSinceLastVisit >= 3) {
      return { primary: `You came back, ${name}.`, secondary: 'No catching up needed. Just come as you are.' };
    }
    if (hour < 12) {
      return { primary: `Good morning, ${name}.`, secondary: 'A new day to choose yourself.' };
    } else if (hour < 17) {
      return { primary: `Good afternoon, ${name}.`, secondary: 'You are exactly where you need to be.' };
    }
    return { primary: `Good evening, ${name}.`, secondary: 'You made it through another day. That matters.' };
  };

  const getStreakDays = () => {
    const days = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0);
      days.push({ day: dayName, isToday: i === 0, isCompleted: i >= (7 - (dashboard?.streak || 0)) });
    }
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FBF9F6' }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 animate-pulse" style={{ 
            background: 'radial-gradient(circle, rgba(201,168,76,0.4) 0%, rgba(232,180,184,0.2) 100%)',
            boxShadow: '0 0 40px rgba(201,168,76,0.3)'
          }} />
          <p style={{ color: '#6B6878', fontFamily: 'Jost, sans-serif' }}>
            Preparing your sanctuary...
          </p>
        </div>
      </div>
    );
  }

  const greeting = getGreeting();
  const phase = dashboard?.phase || 1;
  const currentPhaseInfo = phaseInfo[phase];
  const streakDays = getStreakDays();

  return (
    <div className="min-h-screen pb-24 relative overflow-hidden" style={{ backgroundColor: '#FBF9F6' }} data-testid="sanctuary-page">
      <MilestoneCelebration streak={dashboard?.streak || 0} />
      
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at center, rgba(201,168,76,0.04) 0%, transparent 70%)',
        animation: 'breathe 8s ease-in-out infinite',
      }} />
      
      {/* Noise Texture */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      <main className="relative z-10 max-w-2xl mx-auto px-6 py-8">
        {/* Sage Greeting */}
        <SageGreeting greeting={greeting} animateIn={animateIn} />

        {/* Emotional Weather */}
        <EmotionalWeather 
          selectedWeather={selectedWeather}
          weatherResponse={weatherResponse}
          onWeatherSelect={handleWeatherSelect}
          animateIn={animateIn}
        />

        {/* Today's Reflection */}
        <section className={`mb-10 transition-all duration-700 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '0.3s' }}>
          <button
            onClick={() => navigate('/chat', { state: { prompt: dashboard?.daily_prompt?.prompt_text } })}
            className="w-full text-left p-6 rounded-2xl relative overflow-hidden group transition-all duration-300 hover:scale-[1.01]"
            style={{ background: '#F9F4F0' }}
            data-testid="daily-prompt-card"
          >
            <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
              border: '1px solid transparent',
              background: 'linear-gradient(90deg, rgba(201,168,76,0.3), rgba(232,180,184,0.2), rgba(201,168,76,0.3)) border-box',
              WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              animation: 'shimmer 4s ease-in-out infinite',
            }} />
            <span className="text-xs tracking-[0.15em] block mb-4" style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}>
              YOUR REFLECTION FOR TODAY
            </span>
            <p className="text-lg md:text-xl leading-relaxed mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
              {dashboard?.daily_prompt?.prompt_text || 'What is your heart asking you to notice today?'}
            </p>
            <span className="text-sm group-hover:translate-x-1 inline-flex items-center transition-transform duration-300" style={{ color: '#A9756B' }}>
              Explore with Sage →
            </span>
          </button>
        </section>

        {/* Ritual Tiles */}
        <RitualTiles animateIn={animateIn} />

        {/* Streak Display */}
        <StreakDisplay streak={dashboard?.streak || 0} streakDays={streakDays} animateIn={animateIn} />

        {/* Phase Card */}
        <section className={`mb-10 transition-all duration-700 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '0.6s' }}>
          <div className="p-6 rounded-2xl" style={{ background: 'rgba(138,158,143,0.08)', border: '1px solid rgba(138,158,143,0.2)' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs tracking-[0.1em]" style={{ color: '#72826F', fontFamily: 'Jost, sans-serif' }}>CURRENT PHASE</span>
              <button onClick={() => navigate('/phase-reassessment')} className="text-xs underline transition-opacity hover:opacity-70" style={{ color: '#8A8798', fontFamily: 'Jost, sans-serif' }}>
                Check progress
              </button>
            </div>
            <h3 className="text-xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
              Phase {phase}: {currentPhaseInfo?.name}
            </h3>
            <p className="text-sm mb-4" style={{ fontFamily: 'Jost, sans-serif', color: '#6B6878' }}>
              {currentPhaseInfo?.description}
            </p>
            <div className="h-0.5 rounded-full mb-4" style={{ background: 'rgba(138,158,143,0.2)' }}>
              <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${(phase / 4) * 100}%`, background: '#C9A84C' }} />
            </div>
            <a href={currentPhaseInfo?.workbookUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm transition-all duration-300 hover:gap-3" style={{ color: '#A9756B', fontFamily: 'Jost, sans-serif' }} data-testid="current-phase-workbook">
              <Download className="w-4 h-4" />
              Download Phase {phase} Workbook
            </a>
          </div>
        </section>

        {/* Referral Card */}
        {referralCode && (
          <section className={`mb-10 transition-all duration-700 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '0.7s' }}>
            <div className="p-6 rounded-2xl relative overflow-hidden" style={{ 
              background: 'linear-gradient(135deg, rgba(232,180,184,0.08) 0%, rgba(201,168,76,0.06) 100%)',
              border: '1px solid rgba(232,180,184,0.15)',
            }}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'radial-gradient(circle, rgba(232,180,184,0.3) 0%, rgba(232,180,184,0.1) 100%)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#A9756B" strokeWidth="1.5">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg mb-1" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
                    Invite someone who needs this
                  </h3>
                  <p className="text-sm mb-4" style={{ fontFamily: 'Jost, sans-serif', color: '#6B6878' }}>
                    Give your people 21 days free. You get 7 days on us when they join.
                  </p>
                  <div className="flex items-center gap-3">
                    <button onClick={handleCopyReferral} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-300 hover:scale-[1.02]" style={{ 
                      background: copySuccess ? 'rgba(138,158,143,0.3)' : 'rgba(232,180,184,0.15)',
                      color: copySuccess ? '#72826F' : '#e8b4b8',
                      fontFamily: 'Jost, sans-serif'
                    }} data-testid="referral-copy-btn">
                      {copySuccess ? (
                        <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>Copied!</>
                      ) : (
                        <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy Link</>
                      )}
                    </button>
                    <button onClick={() => navigate('/settings', { state: { section: 'referral' } })} className="text-xs underline transition-opacity hover:opacity-70" style={{ color: '#8A8798', fontFamily: 'Jost, sans-serif' }}>
                      View details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNav currentPage="home" />

      {/* Crisis Footer */}
      <div className="fixed bottom-20 left-0 right-0 z-50">
        <CrisisFooter />
      </div>

      {/* Animation Styles */}
      <style>{`
        @keyframes breathe { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.02); opacity: 0.95; } }
        @keyframes pulse-glow { 0%, 100% { box-shadow: 0 0 30px rgba(201,168,76,0.4), 0 0 60px rgba(232,180,184,0.2); transform: scale(1); } 50% { box-shadow: 0 0 40px rgba(201,168,76,0.6), 0 0 80px rgba(232,180,184,0.3); transform: scale(1.05); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes fade-in { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
        .sage-orb { animation: pulse-glow 3s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default SanctuaryPage;
